import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useWallet } from '../../context/WalletContext';
import { useNotify } from '../../context/NotificationContext';
import { CONTRACTS } from '../../config/contracts';
import { CURRENT_NETWORK } from '../../config/networks';
import { shortenAddress } from '../../utils/format';
import { parseError } from '../../utils/ethers';
import { Spinner } from '../common/Loader';

const ZERO = '0x0000000000000000000000000000000000000000';

/**
 * Health check for the deployed contracts.
 * The Xterra contract is a SINGLE deployment now — no more Storage/Logic split.
 */
export default function ContractHealthPanel() {
  const { read, write } = useContracts();
  const { account } = useWallet();
  const notify = useNotify();

  const [xtOwner, setXtOwner] = useState(null);
  const [xtMinter, setXtMinter] = useState(null);

  const [checks, setChecks] = useState({
    xterra:   { status: 'checking', detail: '', extra: '' },
    paused:   { status: 'checking', detail: '', extra: '' },
    usdt:     { status: 'checking', detail: '', extra: '' },
    xt:       { status: 'checking', detail: '', extra: '' },
    xtMint:   { status: 'checking', detail: '', extra: '' },
    pools:    { status: 'checking', detail: '', extra: '' },
    poolLink: { status: 'checking', detail: '', extra: '' },
  });
  const [poolsLinkedAddr, setPoolsLinkedAddr] = useState(null); // xterra.poolsContract() value
  const [busy, setBusy] = useState('');

  const [depths, setDepths] = useState({ prop: '', gen: '' });
  const [depthsCurrent, setDepthsCurrent] = useState({ prop: '', gen: '' });

  const loadDepths = () => {
    // maxPropagationDepth / maxGenerationDepth removed in new Xterra.sol.
    // Keeping empty for backward compat.
    setDepths({ prop: '', gen: '' });
    setDepthsCurrent({ prop: '', gen: '' });
  };
  useEffect(() => { loadDepths(); }, []);

  const check = async () => {
    setChecks({
      xterra:   { status: 'checking', detail: '', extra: '' },
      paused:   { status: 'checking', detail: '', extra: '' },
      usdt:     { status: 'checking', detail: '', extra: '' },
      xt:       { status: 'checking', detail: '', extra: '' },
      xtMint:   { status: 'checking', detail: '', extra: '' },
      pools:    { status: 'checking', detail: '', extra: '' },
      poolLink: { status: 'checking', detail: '', extra: '' },
    });

    // Xterra contract
    let paused = null;
    try {
      const [owner, admin] = await Promise.all([read.storage.owner(), read.storage.admin()]);
      try { paused = await read.storage.paused(); } catch {}
      setChecks((s) => ({
        ...s,
        xterra: {
          status: 'ok',
          detail: 'Xterra contract reachable',
          extra: `Owner: ${shortenAddress(owner)} · Admin: ${shortenAddress(admin)}`,
        },
      }));
    } catch (err) {
      setChecks((s) => ({
        ...s,
        xterra: {
          status: 'error',
          detail: 'Xterra contract not reachable',
          extra: err?.message?.slice(0, 80) || '',
        },
      }));
    }

    // Pause status
    if (paused === true) {
      setChecks((s) => ({
        ...s,
        paused: { status: 'warn', detail: 'Platform is PAUSED', extra: 'Users cannot register, invest, mine, or withdraw.' },
      }));
    } else if (paused === false) {
      setChecks((s) => ({
        ...s,
        paused: { status: 'ok', detail: 'Platform is live', extra: 'All actions enabled.' },
      }));
    } else {
      setChecks((s) => ({ ...s, paused: { status: 'error', detail: 'Could not read paused()', extra: '' } }));
    }

    // USDT
    try {
      const [sym, dec] = await Promise.all([read.usdt.symbol(), read.usdt.decimals()]);
      setChecks((s) => ({
        ...s,
        usdt: { status: 'ok', detail: `${sym} · ${dec} decimals`, extra: shortenAddress(CONTRACTS.USDT) },
      }));
    } catch (err) {
      setChecks((s) => ({
        ...s,
        usdt: { status: 'error', detail: 'Not reachable', extra: err?.message?.slice(0, 80) || '' },
      }));
    }

    // XT
    try {
      const [sym, dec] = await Promise.all([read.xt.symbol(), read.xt.decimals()]);
      setChecks((s) => ({
        ...s,
        xt: { status: 'ok', detail: `${sym} · ${dec} decimals`, extra: shortenAddress(CONTRACTS.XT) },
      }));
    } catch (err) {
      setChecks((s) => ({
        ...s,
        xt: { status: 'error', detail: 'Not reachable', extra: err?.message?.slice(0, 80) || '' },
      }));
    }

    // XT MINTER — must equal the Xterra contract (since Xterra mints XT on claim)
    try {
      const [minter, owner] = await Promise.all([read.xt.minter(), read.xt.owner()]);
      setXtMinter(minter);
      setXtOwner(owner);

      const minterLower = String(minter || '').toLowerCase();
      const xterraLower = CONTRACTS.Xterra.toLowerCase();

      if (minterLower === ZERO || minterLower === '') {
        setChecks((s) => ({
          ...s,
          xtMint: {
            status: 'warn',
            detail: 'XT minter is NOT SET',
            extra: 'Set Xterra contract as minter using the button below.',
          },
        }));
      } else if (minterLower === xterraLower) {
        setChecks((s) => ({
          ...s,
          xtMint: {
            status: 'ok',
            detail: 'Xterra is the XT minter',
            extra: 'Mining harvest will succeed.',
          },
        }));
      } else {
        setChecks((s) => ({
          ...s,
          xtMint: {
            status: 'warn',
            detail: 'XT minter is a DIFFERENT address',
            extra: `Current: ${shortenAddress(minter)} · Expected: ${shortenAddress(CONTRACTS.Xterra)}`,
          },
        }));
      }
    } catch (err) {
      setChecks((s) => ({
        ...s,
        xtMint: {
          status: 'error',
          detail: 'Could not read XT minter',
          extra: err?.message?.slice(0, 100) || '',
        },
      }));
    }

    // ============================================
    // POOLS CONTRACT reachable?
    // ============================================
    const configuredPools = CONTRACTS.XterraPools;
    const configuredIsSet = configuredPools && configuredPools !== ZERO;

    if (!configuredIsSet) {
      setChecks((s) => ({
        ...s,
        pools: {
          status: 'warn',
          detail: 'Pools contract NOT configured',
          extra: 'Set XterraPools address in src/config/contracts.js.',
        },
        poolLink: {
          status: 'warn',
          detail: 'Cannot check — Pools not configured',
          extra: '',
        },
      }));
    } else if (!read.pools) {
      setChecks((s) => ({
        ...s,
        pools: {
          status: 'error',
          detail: 'Pools instance missing',
          extra: 'Reload the app so the useContracts hook picks up the address.',
        },
        poolLink: { status: 'error', detail: 'Cannot check', extra: '' },
      }));
    } else {
      // Pools reachable check
      try {
        const [, , , , ] = await read.pools.getAllPoolStatus(); // just to prove reachability
        setChecks((s) => ({
          ...s,
          pools: {
            status: 'ok',
            detail: 'Pools contract reachable',
            extra: shortenAddress(configuredPools),
          },
        }));
      } catch (err) {
        setChecks((s) => ({
          ...s,
          pools: {
            status: 'error',
            detail: 'Pools contract not reachable',
            extra: err?.message?.slice(0, 80) || '',
          },
        }));
      }

      // Xterra → Pools link check
      try {
        const linked = await read.xterra.poolsContract();
        setPoolsLinkedAddr(linked);

        const linkedLower = String(linked || '').toLowerCase();
        const configuredLower = configuredPools.toLowerCase();

        if (linkedLower === ZERO) {
          setChecks((s) => ({
            ...s,
            poolLink: {
              status: 'warn',
              detail: 'Xterra ↔ Pools NOT LINKED',
              extra: 'Pool cuts are not being accumulated. Link them using the button below.',
            },
          }));
        } else if (linkedLower === configuredLower) {
          setChecks((s) => ({
            ...s,
            poolLink: {
              status: 'ok',
              detail: 'Xterra ↔ Pools linked',
              extra: 'Investments will notify Pools automatically.',
            },
          }));
        } else {
          setChecks((s) => ({
            ...s,
            poolLink: {
              status: 'warn',
              detail: 'Xterra points to a DIFFERENT Pools address',
              extra: `On-chain: ${shortenAddress(linked)} · Configured: ${shortenAddress(configuredPools)}`,
            },
          }));
        }
      } catch (err) {
        setChecks((s) => ({
          ...s,
          poolLink: {
            status: 'error',
            detail: 'Could not read xterra.poolsContract()',
            extra: err?.message?.slice(0, 80) || '',
          },
        }));
      }
    }
  };

  useEffect(() => { check(); /* eslint-disable-next-line */ }, []);

  async function setXterraAsXtMinter() {
    if (!write) return notify.error('Wallet not connected.');
    if (!ethers.utils.isAddress(CONTRACTS.Xterra) || CONTRACTS.Xterra === ZERO) {
      return notify.error('Configured Xterra address is invalid.');
    }
    if (xtOwner && account && xtOwner.toLowerCase() !== account.toLowerCase()) {
      return notify.error(`Only the XT owner can set the minter. Owner: ${shortenAddress(xtOwner)}`);
    }
    setBusy('setMinter');
    try {
      const tx = await write.xt.setMinter(CONTRACTS.Xterra);
      notify.info('Setting Xterra as XT minter...');
      await tx.wait();
      notify.success('Xterra is now the XT minter.');
      check();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy('');
    }
  }

  async function linkPoolsToXterra() {
    if (!write) return notify.error('Wallet not connected.');
    const target = CONTRACTS.XterraPools;
    if (!ethers.utils.isAddress(target) || target === ZERO) {
      return notify.error('Configured Pools address is invalid.');
    }
    setBusy('linkPools');
    try {
      const tx = await write.xterra.setPoolsContract(target);
      notify.info('Linking Pools to Xterra...');
      await tx.wait();
      notify.success('Pools linked to Xterra.');
      check();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy('');
    }
  }

  async function togglePause() {
    if (!write) return notify.error('Wallet not connected.');
    const currentlyPaused = checks.paused.status === 'warn';
    setBusy('pause');
    try {
      const tx = await write.storage.setPaused(!currentlyPaused);
      notify.info(currentlyPaused ? 'Unpausing...' : 'Pausing...');
      await tx.wait();
      notify.success(currentlyPaused ? 'Platform resumed.' : 'Platform paused.');
      check();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="admin-panel">
      <h3>Contract Health</h3>
      <p className="panel-hint">
        Live check of all deployed contracts on {CURRENT_NETWORK.displayName}.
      </p>

      <div style={{ marginBottom: 16 }}>
        <button className="btn btn-secondary btn-sm" onClick={check}>↻ Re-check</button>
      </div>

      <CheckRow label="Xterra Contract"    addr={CONTRACTS.Xterra} c={checks.xterra} />
      <CheckRow label="Platform Status"    c={checks.paused} isLink />
      <CheckRow label="USDT Token"         addr={CONTRACTS.USDT} c={checks.usdt} />
      <CheckRow label="XT Token"           addr={CONTRACTS.XT}   c={checks.xt} />
      <CheckRow label="XT Mint Permission" c={checks.xtMint} isLink />
      <CheckRow label="Pools Contract"     addr={CONTRACTS.XterraPools} c={checks.pools} />
      <CheckRow label="Xterra ↔ Pools Link" c={checks.poolLink} isLink />

      {/* Pause toggle */}
      {(checks.paused.status === 'ok' || checks.paused.status === 'warn') && (
        <div style={{ marginTop: 16 }}>
          <button
            className={`btn ${checks.paused.status === 'warn' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={togglePause}
            disabled={busy === 'pause'}
          >
            {busy === 'pause' ? <Spinner /> : (checks.paused.status === 'warn' ? '▶ Resume Platform' : '⏸ Pause Platform')}
          </button>
        </div>
      )}

      {/* XT MINTER SETUP */}
      {checks.xtMint.status !== 'checking' && (
        <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid var(--border-soft)' }}>
          <h4 style={{ marginBottom: 4 }}>XT Token Minter</h4>
          <p className="panel-hint">
            The Xterra contract must be the XT minter for mining harvests to succeed.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
            <InfoTile label="XT Owner" value={xtOwner ? shortenAddress(xtOwner) : '—'} />
            <InfoTile
              label="Current XT Minter"
              value={xtMinter && xtMinter.toLowerCase() !== ZERO ? shortenAddress(xtMinter) : 'Not set'}
              warn={!xtMinter || xtMinter.toLowerCase() === ZERO}
            />
            <InfoTile label="Should Be (Xterra)" value={shortenAddress(CONTRACTS.Xterra)} accent />
          </div>

          {checks.xtMint.status === 'ok' ? (
            <div style={{ padding: 14, background: 'rgba(0, 229, 160, 0.06)', border: '1px solid rgba(0, 229, 160, 0.3)', borderRadius: 12 }}>
              <div style={{ color: 'var(--accent)', fontWeight: 700, marginBottom: 2 }}>✓ Xterra is the XT minter</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Mining harvests will succeed.</div>
            </div>
          ) : (
            <div style={{ padding: 16, background: 'rgba(255, 176, 32, 0.08)', border: '1px solid rgba(255, 176, 32, 0.35)', borderRadius: 12 }}>
              <div style={{ color: 'var(--warning)', fontWeight: 700, marginBottom: 6 }}>
                ⚠ Action Required — Xterra is not the XT minter
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
                Users cannot harvest mining rewards until Xterra is set as the XT minter.
                Only the <strong>XT token owner</strong> can perform this action.
                {xtOwner && account && xtOwner.toLowerCase() !== account.toLowerCase() && (
                  <> Your wallet is not the XT owner — switch to <strong>{shortenAddress(xtOwner)}</strong>.</>
                )}
              </div>
              <button
                className="btn btn-primary btn-block"
                onClick={setXterraAsXtMinter}
                disabled={busy === 'setMinter' || (xtOwner && account && xtOwner.toLowerCase() !== account.toLowerCase())}
              >
                {busy === 'setMinter' ? <><Spinner /> Setting minter...</> : '🔑 Set Xterra as XT Minter'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* POOLS CONTRACT SETUP */}
      {checks.poolLink.status !== 'checking' && CONTRACTS.XterraPools !== ZERO && (
        <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid var(--border-soft)' }}>
          <h4 style={{ marginBottom: 4 }}>Pools Contract Link</h4>
          <p className="panel-hint">
            The Pools contract must be linked to Xterra so investments feed the pool turnover pots.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
            <InfoTile
              label="On-chain Pools"
              value={poolsLinkedAddr && poolsLinkedAddr.toLowerCase() !== ZERO ? shortenAddress(poolsLinkedAddr) : 'Not set'}
              warn={!poolsLinkedAddr || poolsLinkedAddr.toLowerCase() === ZERO}
            />
            <InfoTile
              label="Configured Pools"
              value={shortenAddress(CONTRACTS.XterraPools)}
              accent
            />
          </div>

          {checks.poolLink.status === 'ok' ? (
            <div style={{ padding: 14, background: 'rgba(0, 229, 160, 0.06)', border: '1px solid rgba(0, 229, 160, 0.3)', borderRadius: 12 }}>
              <div style={{ color: 'var(--accent)', fontWeight: 700, marginBottom: 2 }}>✓ Pools linked to Xterra</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Every real investment now feeds the X1 / X2 / X3 pool accumulators.
              </div>
            </div>
          ) : (
            <div style={{ padding: 16, background: 'rgba(255, 176, 32, 0.08)', border: '1px solid rgba(255, 176, 32, 0.35)', borderRadius: 12 }}>
              <div style={{ color: 'var(--warning)', fontWeight: 700, marginBottom: 6 }}>
                ⚠ Action Required — Pools not linked
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
                Until the Xterra contract's <code>poolsContract</code> pointer matches the configured
                Pools address, investments will NOT accumulate into the pools.
                Only owner or admin can call this.
              </div>
              <button
                className="btn btn-primary btn-block"
                onClick={linkPoolsToXterra}
                disabled={busy === 'linkPools'}
              >
                {busy === 'linkPools' ? <><Spinner /> Linking...</> : '🔗 Link Pools to Xterra'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoTile({ label, value, warn, accent }) {
  return (
    <div style={{ padding: 14, background: 'var(--bg-tertiary)', border: '1px solid var(--border-soft)', borderRadius: 12 }}>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>
        {label}
      </div>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 13, marginTop: 6,
        color: warn ? 'var(--warning)' : accent ? 'var(--accent)' : 'var(--text-primary)',
      }}>
        {value}
      </div>
    </div>
  );
}

function CheckRow({ label, addr, c, isLink }) {
  const statusColors = {
    checking: { bg: 'var(--bg-tertiary)',                border: 'var(--border-soft)', color: 'var(--text-muted)', icon: '⋯' },
    ok:       { bg: 'rgba(0, 229, 160, 0.06)',           border: 'rgba(0, 229, 160, 0.3)', color: 'var(--accent)', icon: '✓' },
    warn:     { bg: 'rgba(255, 176, 32, 0.08)',          border: 'rgba(255, 176, 32, 0.35)', color: 'var(--warning)', icon: '⚠' },
    error:    { bg: 'rgba(255, 77, 94, 0.06)',           border: 'rgba(255, 77, 94, 0.35)', color: 'var(--error)', icon: '✕' },
  };
  const style = statusColors[c.status] || statusColors.checking;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '40px 1fr auto',
      gap: 14,
      alignItems: 'center',
      padding: '14px 18px',
      background: style.bg,
      border: `1px solid ${style.border}`,
      borderRadius: 12,
      marginBottom: 10,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: style.bg, border: `1px solid ${style.border}`,
        color: style.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, fontWeight: 800,
      }}>
        {c.status === 'checking' ? <Spinner /> : style.icon}
      </div>
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{label}</div>
        <div style={{ fontSize: 12, color: style.color, marginTop: 2, fontWeight: 600 }}>
          {c.detail}
        </div>
        {c.extra && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3, fontFamily: 'var(--font-mono)' }}>
            {c.extra}
          </div>
        )}
      </div>
      {!isLink && addr && addr !== ethers.constants.AddressZero && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>
          {shortenAddress(addr)}
        </div>
      )}
    </div>
  );
}
